import { useCallback, useState } from "react";

import { parseReference } from "@medplum/core";
import { LabOrganization } from "@medplum/health-gorilla-core";
import { AsyncAutocomplete, AsyncAutocompleteProps, useMedplum } from "@medplum/react";

import { Pill, TextInput } from "@mantine/core";

import DisabledReason from "@/components/DisabledReason";
import { OrganizationType } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { resourceToOption } from "@/utils/autocomplete";
import { getResourcesFromBundle, joinTokenString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

interface PerformingLabSearchInputProps
  extends Omit<AsyncAutocompleteProps<LabOrganization>, "toOption" | "loadOptions"> {
  defaultValue: LabOrganization | undefined;
  canRemove: boolean;
  accounts: string[];
  isLoading?: boolean;
  tooltipLabel?: string;
}

export default function PerformingLabSearchInput({
  defaultValue,
  canRemove,
  onChange,
  isLoading,
  accounts,
  tooltipLabel = "Please remove all selected tests before changing the performing lab",
  ...props
}: PerformingLabSearchInputProps) {
  const medplum = useMedplum();
  const [noAvailableOptions, setNoAvailableOptions] = useState(false);

  const loadOptions = useCallback(
    async (query: string) => {
      try {
        const accountIds = accounts
          .map((account) => parseReference({ reference: account })?.[1])
          .filter(Boolean);

        if (!accountIds.length) {
          setNoAvailableOptions(true);
          return [];
        }

        const { bundle } = await medplum.searchResources("Organization", {
          name: query,
          type: joinTokenString(Systems.OrganizationType, OrganizationType.HealthCareProvider),
          identifier: `${Systems.HealthGorilla}|`,
          _id: accountIds.join(","),
        });
        return getResourcesFromBundle<LabOrganization>("Organization", bundle);
      } catch (error) {
        showSystemErrorNotification();
        throw error;
      }
    },
    [medplum, accounts],
  );

  if (isLoading) {
    return <TextInput disabled label="Performing Lab" placeholder="Loading..." />;
  }

  if (noAvailableOptions) {
    return <TextInput disabled label="Performing Lab" placeholder="No available options" />;
  }

  return (
    <AsyncAutocomplete<LabOrganization>
      maxValues={1}
      onChange={onChange}
      label="Performing Lab"
      loadOptions={loadOptions}
      toOption={resourceToOption}
      defaultValue={defaultValue}
      placeholder="Search to choose a performing lab"
      pillComponent={({ onRemove, item }) =>
        canRemove ? (
          <Pill
            withRemoveButton
            onRemove={onRemove}
            removeButtonProps={{ "aria-label": "Remove Performing Lab" }}
          >
            {item.label}
          </Pill>
        ) : (
          <DisabledReason reason={tooltipLabel}>
            <Pill>{item.label}</Pill>
          </DisabledReason>
        )
      }
      {...props}
    />
  );
}
